import {
    gsap,
    Back,
    Power1,
    Power2
} from 'gsap'
import {
    InsertAnalyticsEventType,
    InsertEventType,
    OverlayEventType,
    LogType,
    InsertType,
    InsertOverrideType,
    DebugURLParams
} from "../../../../../common/schemas/enums";
import {
    LogManager
} from '../../../../services/log-manager';
import {
    CustomTimeout
} from '../../../../services/custom-timeout';
import {
    Title,
    DataAsset,
    Asset,
    Image,
    InsertParsedData
} from '../../../../../common/schemas/public'
import {
    isNil
} from 'lodash-es'
import{
  ColorThief
} from '../../../../../assets/inserts/js/color-thief/color-thief-min.js'

declare global {
    interface String {
        toCamelCase(): string;
    }
}

String.prototype.toCamelCase = function():string {
    return String(this).trim().split(' ').map(s => (s.length>0)?s[0].toUpperCase()+s.substr(1,s.length).trim():"").join(' ');
}

declare let FontFace: any;

type InsertParams = {
    insert: any,
    insertDocument: Document,
    initializedTime: number,
    displayedTime: number,
    minVisibilityTime: number,
    boundDestroy: ()=>{},
    timeoutDestroy: ()=>{},
    isReady: boolean,
    impressionTrackers: string[],
    viewabilityTrackers: string[],
    clickthroughTrackers: string[],
    jsTrackers: string[],
    eventTrackers: {[key: string]: string[]};
    winTracker: string,
    firstLoad: boolean,
    firstCreation: boolean,
    fontCount: number,
    fontsLoaded: number,
    closeTriggered: boolean,
    collapsedByTimer: boolean,
    collapseTimer?: CustomTimeout,
    viewabilityValue: number,
    viewabilityArray: number[],
    viewabilityTimer?: CustomTimeout,
    viewed: boolean,
    insertTimer?: CustomTimeout,
    isPaused: boolean,
    isDestroying: boolean,
    resizeTimer: CustomTimeout
}

export class InsertSharedLogic{
    public lm: LogManager;
    public root = process.env.SERVER_ROOT;
    public fontCount: number = 0;
    public fontsLoaded: number = 0;
    public assetCatapultXBug = this.root + '/assets/inserts/shared/common_images/CatapultX-Bug.png'
    public assetCatapultXBugFlip = this.root + '/assets/inserts/shared/common_images/CatapultX-BugFlip.png'
    public assetCatapultXBugFlipWing = this.root + '/assets/inserts/shared/common_images/CatapultX-BugFlip-Wing.png'
    public assetGenericBug = this.root + '/assets/inserts/shared/common_images/Generic-Bug.png'
    public assetGenericBugFlip = this.root + '/assets/inserts/shared/common_images/Generic-BugFlip.png'
    public assetGenericBugFlipWing = this.root + '/assets/inserts/shared/common_images/Generic-BugFlip-Wing.png'
    public assetOutbrainBug = this.root + '/assets/inserts/shared/common_images/Outbrain-Bug.png'
    public assetOutbrainBugFlip = this.root + '/assets/inserts/shared/common_images/Outbrain-BugFlip.png'
    public assetOutbrainBugFlipWing = this.root + '/assets/inserts/shared/common_images/Outbrain-BugFlip-Wing.png'
    public assetAdChoicesBug = this.root + '/assets/inserts/shared/common_images/AdChoices-Bug.png'
    public assetAdChoicesBugFlip = this.root + '/assets/inserts/shared/common_images/AdChoices-BugFlip.png'
    public assetAdChoicesBugFlipWing = this.root + '/assets/inserts/shared/common_images/AdChoices-BugFlip-Wing.png'
    public assetBrainlyBug = this.root + '/assets/inserts/shared/common_images/Brainly-Bug.png'
    public assetBrainlyBugFlip = this.root + '/assets/inserts/shared/common_images/Brainly-BugFlip.png'
    public assetBrainlyBugFlipWing = this.root + '/assets/inserts/shared/common_images/Brainly-BugFlip-Wing.png'
    public firstAnimationTimeDelay: number = 1;
    public insertsPaused: boolean = false;
    public logoWidth: number = 80;
    public isMobile: boolean = false;

    private _elementHost: HTMLElement;
    private currentBidIDs: string[] = [];
    private isLocal: boolean = false;
    private currentInserts: {[key: string]: InsertParams} = {};
    private preventRightClickMenu: boolean = true;
    private overlayCollapseTimeDelay: number = 15;
    private destroyTimeDelay: number = 30;
    private currentVisibleStart: number;
    private subloadInsertTimer: CustomTimeout;
    private currentDisplayCompleteTimer: CustomTimeout;
    private resumeInsertsTimeout: CustomTimeout;
    private usedTrackersList: string [] = [];
    private allowCloseDelaySeconds: number = 4;
    private colorThief = new ColorThief();

    private boundButtonClick = this.buttonClick.bind(this);
    private boundButtonOver = this.buttonOver.bind(this);
    private boundTouchStart = this.touchStart.bind(this);
    private boundTouchEnd = this.touchEnd.bind(this);
    private boundButtonOut = this.buttonOut.bind(this);
    private boundButtonDown = this.buttonDown.bind(this);
    private boundButtonUp = this.buttonUp.bind(this);
    private boundPreventMenu = this.preventMenu.bind(this);
    private touchTarget: HTMLElement;
    public boundEndInsert = this.endInsert.bind(this);
    private boundOverlayCommunication = this.overlayCommunicationEvent.bind(this);

    private ctaLink: string;
    private assetTitle: Asset;
    private assetDesc: Asset;
    private assetCta: Asset;
    private assetMainImg: Asset[];
    private assetIcon: Asset;

    get currentIDs(): string[]{
        return Object.keys(this.currentInserts);
    }

    constructor(lm?:LogManager) {

        if(!isNil(lm)){
            this.lm = lm;
        }else{
            lm = new LogManager();
        }

        this.subloadInsertTimer = new CustomTimeout(()=>{}, 0, 'Show Subloaded Insert Timer', this.lm, LogType.INSERT);
        this.currentDisplayCompleteTimer = new CustomTimeout(()=>{}, 0, 'Current Display Completed Timer', this.lm, LogType.INSERT);
        this.resumeInsertsTimeout = new CustomTimeout(()=>{}, 0, 'Resume Inserts Timeout Timer', this.lm, LogType.INSERT);

        this.debugLog('InsertSharedLogic initialized', [LogType.QA, LogType.INSERT]);

        this.isLocal = ((window.location.href.indexOf('localhost') >= 0 || window.location.href.indexOf('file:///') >= 0 || window.location.href.indexOf('127.0.0.1') >= 0) ? true : false);
    }

    getDocument(uniqueid:string){
        return (!isNil(this.currentInserts[uniqueid]))?this.currentInserts[uniqueid].insertDocument:null;
    }

    addInsert(ins){

        this.currentInserts[ins.uniqueid]={
            insert: ins,
            insertDocument:ins.el.shadowRoot,
            initializedTime: (new Date()).getTime(),
            displayedTime: -1,
            minVisibilityTime: 0,
            boundDestroy: this.insertDestroyFunction.bind(this, ins.uniqueid),
            timeoutDestroy: this.insertDestroyFunction.bind(this, ins.uniqueid, true),
            isReady: false,
            impressionTrackers: [],
            viewabilityTrackers: [],
            clickthroughTrackers: [],
            jsTrackers: [],
            eventTrackers: {},
            winTracker: null,
            firstLoad: true,
            firstCreation: true,
            fontCount: 0,
            fontsLoaded: 0,
            closeTriggered: false,
            collapsedByTimer: false,
            viewabilityValue: 0,
            viewabilityArray: [],
            viewed: false,
            isPaused: false,
            isDestroying: false,
            resizeTimer: new CustomTimeout(()=>{this.resizeInsert(ins.uniqueid)}, 0, 'Insert Resize Timer')
        }

        const insertType: string = ins.el.tagName.toLowerCase()
        this.debugLog([`Initialized & added new insert:\n  Bid id: ${ins.acceptedbidid}\n  Type: ${insertType}\n  Unique ID: ${ins.uniqueid}\n  Active ID List: ${this.currentIDs}`, 'Document element: ', ins.el], [LogType.QA, LogType.INSERT]);

        if (ins.overrides !== undefined && typeof ins.overrides === 'string' && ins.overrideParams != null) {
            let overrideArray = JSON.parse(ins.overrides);
            for(let index in overrideArray){
                ins.overrideParams[overrideArray[index]['key']]=overrideArray[index]['value'];
            }
        }

        if(!isNil(ins.overrideParams[InsertOverrideType.YOFFSET]) && typeof ins.yOffset !== 'undefined'){
            ins.yOffset = parseInt(ins.overrideParams[InsertOverrideType.YOFFSET]);
        }

        if(this.lm.paramString.indexOf(DebugURLParams.INSERT_ADJUST_POSITION+'=') >= 0){
            ins.position = this.lm.paramArray.find(x => x.indexOf(DebugURLParams.INSERT_ADJUST_POSITION)>=0).split('=')[1].toLowerCase();
        }else if(!isNil(ins.overrideParams[InsertOverrideType.POSITION]) && typeof ins.position !== 'undefined' && typeof ins.insertposition !== 'undefined'){
            ins.position = ins.overrideParams[InsertOverrideType.POSITION].toLowerCase();
        }else if (ins.insertposition != null && ins.insertposition.toLowerCase() !== 'def') {
            ins.position = ins.insertposition.toLowerCase();
        }
        this.debugLog('selected insert position: ' + ins.position);

        ins.insertOnBottom = (ins.position.indexOf('b') >= 0) ? true : false;
        ins.insertOnLeft = (ins.position.indexOf('l') >= 0) ? true : false;

        if (ins.data !== undefined && typeof ins.data === 'string') {
            ins.state = JSON.parse(ins.data)
        } else {
            ins.state = ins.data
        }
    }

    insertIsReady(uniqueid:string){
        //this.debugLog(['Keys list: ', this.currentIDs], LogType.IMPORTANT);
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentInserts[uniqueid].isReady = true;
            if((this.currentIDs.length > 0 && this.currentInserts[uniqueid].insert.isSubloaded)){
                this.showSubloadedInsert(uniqueid);
            }
            this.attachJSTracker(this.currentInserts[uniqueid]);
        }
    }

    showSubloadedInsert(uniqueid:string, forceAnimation: boolean = false): boolean{
        if(!isNil(this.currentInserts[uniqueid])){
            const activeInsert: string = this.currentIDs.find(x => this.currentInserts[x].insert.isSubloaded === false);
            const subloadInsert: InsertParams = this.currentInserts[uniqueid];
            this.debugLog(`showSubloadedInsert: ${uniqueid} - ACTIVE: ${activeInsert} - SUBLOADED: ${uniqueid}`, LogType.INSERT);
            if(subloadInsert != null && activeInsert !== uniqueid){
                const closeTriggered: boolean = (activeInsert != null)?this.currentInserts[activeInsert].closeTriggered:false;
                if(((!closeTriggered && this.currentDisplayCompleteTimer.timeRemainingInSeconds <= 0) || subloadInsert.insert.insertConfigs.forceLoad || typeof activeInsert === 'undefined') && !this.insertsPaused){
                    if(activeInsert != null){
                        this.debugLog([`Active insert ${activeInsert} minimum viewable time requirement met. Show subloaded insert ${uniqueid}.`, this.currentInserts?.[activeInsert]?.insert, subloadInsert?.insert], [LogType.QA, LogType.INSERT]);
                        const stopActiveClick = this.currentInserts[activeInsert].insertDocument.querySelectorAll(".hotspot");
                        stopActiveClick.forEach(hs => {
                            this.debugLog('Disallowing click: ' + hs.id, LogType.INSERT);
                            gsap.set(hs, {display:'none'});
                        })
                        const destroyActiveInsert = this.currentInserts[activeInsert].boundDestroy;
                        gsap.to(this.currentInserts[activeInsert].insert.host, {duration:.9, opacity:0, onComplete:destroyActiveInsert, ease:Power1.easeIn});
                    }else{
                        this.debugLog([`Active insert not found. Show subloaded insert ${subloadInsert}.`, subloadInsert.insert], [LogType.QA, LogType.INSERT]);
                    }
                    gsap.to(subloadInsert.insert.host, {duration:.9, opacity:1, ease:Power2.easeOut});
                    subloadInsert.insert.shouldStart = true;
                    subloadInsert.insert.beginInsert(forceAnimation);
                    subloadInsert.insert.isSubloaded = false;
                    this.subloadInsertTimer.cancel();
                    return true;
                }else{
                    const delayTime: number = (this.insertsPaused || closeTriggered)?1000:this.currentDisplayCompleteTimer.timeRemainingInSeconds*1000;
                    const delayReasoning: string = (this.insertsPaused)?'Insert display has been suspended.':(closeTriggered)?'Active insert has been user closed.':'Active insert has not met visible time requirements';
                    this.debugLog(`Show subload insert attempted. ${delayReasoning} Will try again in ${delayTime/1000} second(s).`, [LogType.QA]);
                    this.subloadInsertTimer.reset(delayTime, ()=>{this.showSubloadedInsert(uniqueid)})
                }
            }else{
                const subloadErrorString: string = (subloadInsert === null)?' Subloaded insert no longer exists.':' Subloaded insert is already active insert.';
                this.debugLog('Attempted to show subloaded insert.'+subloadErrorString, [LogType.INSERT, LogType.QA]);
            }
        }else{
            this.debugLog('Subloaded insert ' + uniqueid + ' no longer exists. Will not show subload.', [LogType.INSERT]);
        }
        return false;
    }

    initializeIntersectionObserver(currentInsert){
        try{
            let threshholdArray = [];
            for(var i = 0; i <= 100; i++){
              threshholdArray.push(i/100);
            }
            let inView = false;
            let constantViewability = new IntersectionObserver((entries) => {
              entries.forEach(entry => {
                currentInsert.viewabilityValue = entry.intersectionRatio;
                if(!currentInsert.isPaused){
                    if(entry.intersectionRatio >= .5 && !inView) {
                        currentInsert.viewabilityTimer.resume();
                        inView = true;
                    }
                    
                    if(entry.intersectionRatio < .5 && inView){
                        currentInsert.viewabilityTimer.pause();
                        inView = false;
                    }
                } else {
                    currentInsert.viewabilityTimer.pause();
                }
    
              });
            }, {threshold: threshholdArray});
            constantViewability.observe(currentInsert.insertDocument.querySelector('#insertHolder'));
        }catch(err){
            this.debugLog('Unable to initialize IntersectionObserver for insert. IntersectionObserver is not supported on this browser.', [LogType.QA, LogType.ERROR]);
        }
    }

    setInsertDisplay(uniqueid:string){
        if(!isNil(this.currentInserts[uniqueid])){
            let currentInsert = this.currentInserts[uniqueid].insert;
            this.currentInserts[uniqueid].minVisibilityTime = currentInsert.insertConfigs.carouselSpeed * 1000;
            gsap.set(currentInsert.host, {display: 'block'})
            if(currentInsert.isSubloaded){
                gsap.set(currentInsert.host, {opacity:0});
                this.debugLog(`Subloaded insert ${currentInsert.el.tagName.toLowerCase()} - visibility hidden until ready to show.`, LogType.INSERT);
                currentInsert.shouldStart = false;
            }
        }
    }

    set elementHost(elm: HTMLElement){
        if(elm === null){
            if(this._elementHost !== null && typeof this._elementHost !== 'undefined'){
                this._elementHost.removeEventListener(OverlayEventType.EVENT_FROM_OVERLAY, this.boundOverlayCommunication);
            }
        }else{
            this._elementHost = elm;
            this._elementHost.addEventListener(OverlayEventType.EVENT_FROM_OVERLAY, this.boundOverlayCommunication, true);
        }
    }

    removeCurrentInsert(uniqueid: string){
        this.debugLog([`removeCurrentInsert: ${uniqueid}`], LogType.INSERT);
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentInserts[uniqueid] = null;
            delete this.currentInserts[uniqueid];
        }
    }

    debugLog(l: string | any[], type: LogType | LogType[]=LogType.INSERT) {
        this.lm.evaluateDebugLog(l, type);
    }

    getStyle(uniqueid: string, elem:HTMLElement | string, prop: string):number {
        if(!isNil(this.currentInserts[uniqueid])){
            const currentDocument = this.currentInserts[uniqueid].insertDocument;
            const element: HTMLElement = (typeof elem === 'string')?currentDocument.getElementById(elem):elem;
            if(!isNil(element)){
                const s:CSSStyleDeclaration = window.getComputedStyle(element);
                const val:number = Math.round(parseFloat(s.getPropertyValue(prop)));
                if(isNaN(val)){
                    this.debugLog('getStyle: Not a number: ' + element?.id + ' - ' + prop, [LogType.CXO, LogType.ERROR]);
                }
                return (isNaN(val))?0:val;
            }else{
                return null;
            }
        }
    }

    getStyleString(uniqueid: string, elem:HTMLElement | string, prop: string):string {
        if(!isNil(this.currentInserts[uniqueid])){
            const currentDocument = this.currentInserts[uniqueid].insertDocument;
            const element: HTMLElement = (typeof elem === 'string')?currentDocument.getElementById(elem):elem;
            if(!isNil(element)){
                const s:CSSStyleDeclaration = window.getComputedStyle(element);
                return s.getPropertyValue(prop) || "";
            }else{
                return null;
            }
        }
    }

    loadFont(arr: string[]){
        this.fontCount++;
        const fontLoad = new FontFace(...arr);
        fontLoad.display = 'swap';
        fontLoad.style = 'normal';
        fontLoad.weight = 'normal';
        fontLoad.load().then((res) => {
          document['fonts'].add(res);
          this.fontsLoaded++;
          //this.debugLog('Fonts loaded: ' + this.fontsLoaded);
        }).catch((error: Error) => {
          this.debugLog(['Error on font load: ', error], LogType.ERROR);
            ++this.fontsLoaded
        })
    }

    loadDefaultFont(name: string){
        this.loadFont([name, "url('" + this.root + "/assets/inserts/shared/fonts/"+name+".woff2') format('woff2'), url('" + this.root + "/assets/inserts/shared/fonts/"+name+".woff') format('woff')"]);
    }

    get fontsComplete(){
        const complete = (this.fontsLoaded >= this.fontCount)?true:false;
        if(complete)this.debugLog('All fonts have completed loading.');
        return complete;
    }

    insertContainerReady(uniqueid: string): boolean{
        let ready: boolean = false;
        if(!isNil(this.currentInserts[uniqueid])){
            if(this.getStyle(uniqueid, 'container', 'width') > 50){
                ready = true;
            }
        }
        return ready;
    }

    imagesComplete(uniqueid: string): boolean{
        if(!isNil(this.currentInserts[uniqueid])){
            const currentDocument = this.currentInserts[uniqueid].insertDocument;
            let complete: boolean = true;
            const imgs = currentDocument.querySelectorAll('img');
            for(let i = 0; i < imgs.length; i++){
                if(!imgs[i].classList.contains('impression') && !imgs[i].classList.contains('viewability')){
                    if(!imgs[i].complete){
                        this.debugLog(imgs[i].src + " not loaded.");
                        complete = false;
                        break;
                    }else{
                        this.debugLog(`${imgs[i].id}: ${imgs[i].src} loaded with size ${imgs[i].naturalWidth}x${imgs[i].naturalHeight}.`);
                    }
                }else{
                    //this.debugLog(['Not waiting for trackers to load: ' , imgs[i]], LogType.IMPORTANT);
                }
            }
            if(complete)this.debugLog('All images have completed loading.');
            return complete;
        }else{
            return false;
        }
    }

    setFullsizeImagerySizes(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid])){
            const currentDocument = this.currentInserts[uniqueid].insertDocument;
            const fullsizeImageryContainer: HTMLElement = currentDocument.querySelector('#fullsizeImageryContainer');
            const imgs = (!isNil(fullsizeImageryContainer))?fullsizeImageryContainer.querySelectorAll('img'):[]
            imgs.forEach(img =>{
                this.debugLog(`Setting ${img.id}: ${img.src} to size ${img.naturalWidth}x${img.naturalHeight}.`);
                gsap.set(img, {width:img.naturalWidth+'px', height:img.naturalHeight+'px'});
            })
        }
    }

    isFirstLoad(uniqueid: string): boolean{
        //this.debugLog([`Checking if is first load: ${uniqueid}: ${this.currentInserts[uniqueid].firstLoad}`], LogType.IMPORTANT);
        if(!isNil(this.currentInserts[uniqueid])){
            return this.currentInserts[uniqueid].firstLoad;
        }else{
            return false;
        }
    }

    setInsertInitialized(uniqueId: string): void {
        if(!isNil(this.currentInserts[uniqueId])){
            this.currentInserts[uniqueId].insert.insertInitialized = true;
            const loadContainer = this.currentInserts[uniqueId].insert.loadContainer
            const acceptedbidid = this.currentInserts[uniqueId].insert.acceptedbidid
            this.dispatchInsertEvent({type:InsertEventType.INSERT_INITIALIZED, uid:uniqueId, div: loadContainer, acceptedbidid: acceptedbidid});
        }
    }

    handleLoadComplete(uniqueId: string, isAdLightningEvent: boolean): void {
        if(!isNil(this.currentInserts[uniqueId])){
            if(isAdLightningEvent) {
                this.debugLog('AdLightning accepted ad. Banner load successful.', [LogType.QA, LogType.CXO]);
            } else {
                this.debugLog('Prebid renderAd function triggered from overlay. Banner load successful.', [LogType.QA, LogType.CXO]);                
            }

            this.currentInserts[uniqueId].insert.bannerLoaded = true;
        }
    }

    insertAssignData(uniqueid: string): InsertParsedData{
        if(!isNil(this.currentInserts[uniqueid])){
            this.debugLog('BEGIN PARSE INSERT DATA: ' + uniqueid)
            const currentInsert = this.currentInserts[uniqueid].insert;
            
            if(!this.currentBidIDs.includes(currentInsert.acceptedbidid)){
                this.currentBidIDs.push(currentInsert.acceptedbidid);
                this.dispatchInsertEvent({type:InsertEventType.INSERT_RENDERED, uid:uniqueid});
                if(currentInsert.state.isPrebid){
                    this.debugLog('IS PREBID. CREATED DIV ' + currentInsert.state.divId, LogType.IMPORTANT);
                }
            }else{
                this.currentInserts[uniqueid].firstCreation = false;
                this.dispatchInsertEvent({type:InsertEventType.INSERT_RERENDERED, uid:uniqueid});
                if(!currentInsert.state.isPrebid){
                    this.debugLog([`NOT the first appearance of insert (bid id = ${currentInsert.acceptedbidid}). Do not attach impression trackers and send re-initialization analytics.`, this.currentBidIDs], LogType.QA);
                }
            }

            let returnArray: InsertParsedData = {} as InsertParsedData;

            try{
                this.assetTitle = null;
                this.assetDesc = null;
                this.assetCta = null;
                this.assetIcon = null;
                this.assetMainImg = [];
                this.ctaLink = '';
                if(!isNil(currentInsert.state.assets)){
                    currentInsert.state.assets.forEach( (asset: Asset) => {
                        switch(asset.id){
                            case 1:
                                this.assetTitle = asset;
                                break;
                            case 2:
                                this.assetDesc = asset;
                                break;
                            case 3:
                                this.assetCta = asset;
                                break;
                            case 4:
                                this.assetIcon = asset;
                                break;
                            default:
                                this.assetMainImg.push(asset)
                                break;
                        }
                    })
                }
    
                const title: Title = !isNil(this.assetTitle) ? this.assetTitle.title : null
                const desc: DataAsset = !isNil(this.assetDesc) ? this.assetDesc.data : null
                const cta: DataAsset = !isNil(this.assetCta) ? this.assetCta.data : null
                let img: Image = (!isNil(this.assetMainImg) && this.assetMainImg.length>0) ? this.assetMainImg[0].img : null
                const icon: Image = !isNil(this.assetIcon) ? this.assetIcon.img : null
    
                if(currentInsert.el.tagName.toLowerCase() === InsertType.SQUEEZE_BACK_CUSTOM_FULL_BACKGROUND || currentInsert.el.tagName.toLowerCase() === InsertType.CTV_SQUEEZE_BACK_CUSTOM){
                    this.debugLog(['Current image: ', img]);
                    this.debugLog('Finding Custom Squeezeback Image');
                    let foundImg = this.assetMainImg.find(x => x.img.isSqueezeback);
                    img = (!isNil(foundImg))?foundImg.img:img;
                    this.debugLog(['Found image: ', img]);
                }else if(!isNil(img) && img.isSqueezeback === true && (currentInsert.el.tagName.toLowerCase() !== InsertType.SQUEEZE_BACK_CUSTOM_FULL_BACKGROUND && currentInsert.el.tagName.toLowerCase() !== InsertType.CTV_SQUEEZE_BACK_CUSTOM && currentInsert.el.tagName.toLowerCase() !== InsertType.CTV_SQUEEZE_BACK_CUSTOM_QR)){
                    if(!isNil(icon)){
                        img.w = icon.w;
                        img.h = icon.h;
                        img.url = icon.url;
                        this.debugLog("Removing 'custom squeezeback' main image during Non-Custom Squeezeback insert. Replacing with icon.", [LogType.INSERT, LogType.QA]);
                    }
                }
    
                if (!isNil(currentInsert.state.link)) {
                    if (!isNil(currentInsert.state.link.url) && currentInsert.state.link.url !== '') {
                        this.ctaLink = currentInsert.state.link.url
                    } else if (!isNil(currentInsert.state.link.fallback) && currentInsert.state.link.fallback !== '') {
                        this.ctaLink = currentInsert.state.link.fallback
                    }
                }

                const outbrainLinkout: string = `https://www.outbrain.com/what-is/${(typeof currentInsert.state?.akuid !== 'undefined' && currentInsert.state.akuid !== null && currentInsert.state.akuid !== 'NOT_FOUND')?'?uid='+currentInsert.state.akuid:''}`;
    
                returnArray.clickTag1 = "javascript:window.open('" + this.ctaLink + "');void 0;";
                returnArray.clickTag2 = (currentInsert.insertConfigs.isBranded)?"javascript:window.open('https://www.qortex.ai/');void 0;":(currentInsert.insertConfigs.isOutbrain)?`javascript:window.open('${outbrainLinkout}');void 0;`:"javascript:void 0;";
                returnArray.clickTag3 = `javascript:window.open('${outbrainLinkout}');void 0;`;
                returnArray.clickTag4 = `javascript:window.open('${currentInsert.state?.privacy}');void 0;`;
                //returnArray.clickTrackers = (!isNil(currentInsert.state.link) && !isNil(currentInsert.state.link.clicktrackers))? currentInsert.state.link.clicktrackers : null
                returnArray.cta_value = cta && cta.value ? cta.value : 'LEARN MORE';
                returnArray.desc_value = desc && desc.value ? desc.value: '';
                returnArray.title_value = title && title.text ? title.text : '';
    
                /* Latin check
                    returnArray.cta_value = 'ĻEARN MORĚ';
                    returnArray.desc_value = 'Šubheadļine Ťext Hĕre.';
                    returnArray.title_value = 'Ĥeadline Texť Herę';
                */
                if(currentInsert.insertConfigs.isOutbrain && returnArray.desc_value !== ''){
                    returnArray.title_value += ' - '+returnArray.desc_value;
                    returnArray.desc_value = '';
                }
               
                if(!isNil(currentInsert.state.video) && !isNil(currentInsert.state.video.companionAd)){
                    returnArray.imageURL = this.root + currentInsert.state.video.companionAd;
                    this.currentInserts[uniqueid].eventTrackers = currentInsert.state.video.trackers;
                }else{
                    returnArray.imageURL = (img && img.url) ? img.url : this.root + '/assets/inserts/shared/common_images/Default_Icon.png';
                }
                returnArray.iconURL = (icon && icon.url) ? icon.url : this.root + '/assets/inserts/shared/common_images/Default_Icon.png';
    
                if(!currentInsert.el.tagName.toLowerCase().includes('display')){
                    this.debugLog("CTA: " + returnArray.cta_value, LogType.QA);
                    this.debugLog("Headline: " + returnArray.title_value, LogType.QA);
                    this.debugLog("Subheadline: " + returnArray.desc_value, LogType.QA);
                    this.debugLog(["Primary Image URL: ", returnArray.imageURL], LogType.QA);
                    this.debugLog(["Icon Image URL: ", returnArray.iconURL], LogType.QA);
                    this.debugLog(["Clickthrough URL: ", this.ctaLink], LogType.QA);
                }else{
                    this.debugLog('Banner data (adm): ' + currentInsert?.state?.banner, LogType.QA);
                }
    
                //returnArray.imageURL =  this.root + '/assets/inserts/shared/testing/Subtle_Color_Dark.png';
                //returnArray.iconURL =  this.root + '/assets/inserts/shared/testing/Subtle_Color_Light.png';

                if(currentInsert.insertConfigs.isOutbrain){
                    returnArray.logoImageURL = (currentInsert.insertConfigs.isBranded)?this.assetOutbrainBug:this.assetAdChoicesBug;
                }else if(currentInsert.insertConfigs.isBrainly){
                    returnArray.logoImageURL = this.assetBrainlyBug;
                }else{
                    returnArray.logoImageURL = (currentInsert.insertConfigs.isBranded)?this.assetCatapultXBug:this.assetGenericBug;
                }
                returnArray.closeImageURL = this.root + '/assets/inserts/shared/common_images/PNG_CloseButton.png';
    
                this.currentInserts[uniqueid].clickthroughTrackers = (!isNil(currentInsert.state.link) && !isNil(currentInsert.state.link.clicktrackers))? currentInsert.state.link.clicktrackers : [];
                this.currentInserts[uniqueid].jsTrackers = (!isNil(currentInsert.state.link) && !isNil(currentInsert.state.link.jstrackers))? currentInsert.state.link.jstrackers : [];
                if(this.currentInserts[uniqueid].jsTrackers.length === 0){
                    this.currentInserts[uniqueid].jsTrackers = (!isNil(currentInsert.state.jstracker))? [currentInsert.state.jstracker] : [];
                }
                this.currentInserts[uniqueid].impressionTrackers = (!isNil(currentInsert.state.link) && !isNil(currentInsert.state.link.imptrackers))? currentInsert.state.link.imptrackers : [];
                this.currentInserts[uniqueid].viewabilityTrackers = (!isNil(currentInsert.state.viewabilityTrackers)) ? currentInsert.state.viewabilityTrackers : [];
                this.currentInserts[uniqueid].winTracker = (!isNil(currentInsert.state.link) && !isNil(currentInsert.state.link.wintracker))?currentInsert.state.link.wintracker : null;
                
                this.attachWinTracker(this.currentInserts[uniqueid]);
            }catch(err){
                this.debugLog('Error at insertAssignData: '+ err, [LogType.ERROR]);
                this.sendAnalytics(InsertAnalyticsEventType.ERROR_ASSIGNDATA, uniqueid);
            }
 
            return returnArray;
        }
        return null;
    }

    secondsSinceInitialized(uniqueid: string): number{
        if(!isNil(this.currentInserts[uniqueid])){
            const initializedTime = this.currentInserts[uniqueid].initializedTime;
            let displayTime = (new Date()).getTime();
            return (displayTime-initializedTime)/1000;
        }
    }

    resetInitializedTime(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentInserts[uniqueid].initializedTime = (new Date()).getTime();
        }
    }

    insertDisplayStarted(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentVisibleStart = (new Date()).getTime();
            this.currentInserts[uniqueid].displayedTime = this.currentVisibleStart;
            this.currentInserts[uniqueid].closeTriggered = false;
            
            if(this.currentInserts[uniqueid].firstLoad){
                if(this.usedTrackersList.indexOf(uniqueid)===-1){
                    this.dispatchInsertEvent({type:InsertEventType.INSERT_IMPRESSION, uid:uniqueid, insertViewability:this.currentInserts[uniqueid].viewabilityValue});
                    this.usedTrackersList.push(uniqueid);
                }else{
                    this.debugLog(`Impression event already triggered for insert ${uniqueid}. Will not fire a new impression event.`, LogType.QA);
                }
                this.attachImpressionTrackers(this.currentInserts[uniqueid]);
                this.attachViewabilityTrackers(this.currentInserts[uniqueid]);
                this.dispatchInsertEvent({type:InsertEventType.INSERT_DISPLAY_STARTED, uid:uniqueid, insertViewability:this.currentInserts[uniqueid].viewabilityValue});
                this.setInsertViewabilityTimer(uniqueid);
                this.setInsertLifetimeTimer(uniqueid);
                this.setInsertCollapseTimer(uniqueid);
                this.initializeIntersectionObserver(this.currentInserts[uniqueid]);
                this.currentInserts[uniqueid].firstLoad = false;
                this.currentDisplayCompleteTimer.reset(this.currentInserts[uniqueid].minVisibilityTime)
            }else{
                this.debugLog('Insert has been re-opened. Cancel delayed destroy.', [LogType.QA, LogType.INSERT]);
            }
            if(this.currentInserts[uniqueid].firstCreation){
                this.currentInserts[uniqueid].insert.allowCloseDelay = this.allowCloseDelaySeconds;
                if(!isNil(this.currentInserts[uniqueid].insert.isADX) && this.currentInserts[uniqueid].insert.isADX){
                    this.sendAnalytics(InsertAnalyticsEventType.ADX_DISPLAYED, uniqueid);
                }else{
                    this.sendAnalytics(InsertAnalyticsEventType.DISPLAYED, uniqueid);
                }
                this.currentInserts[uniqueid].firstCreation = false;
            }else{
                this.sendAnalytics(InsertAnalyticsEventType.REDISPLAYED, uniqueid);
            }
            const cb = this.currentInserts[uniqueid].timeoutDestroy;
            gsap.killTweensOf(cb);
        }
    }

    insertCollapsedByTimer(uniqueid: string) {
        this.endInsert(uniqueid); 
        this.currentInserts[uniqueid].collapsedByTimer = true;
        this.debugLog(`Insert ${uniqueid} has been closed by its collapseTimer and will not re-initialize unless manually opened.`, [LogType.QA, LogType.INSERT]);
    }

    requestOSDRefresh(uniqueid: string, div: HTMLElement){
        if(!isNil(this.currentInserts[uniqueid])){
            this.dispatchInsertEvent({
                type:InsertEventType.INSERT_REQUEST_OSD_REFRESH,
                uid:uniqueid,
                div: div
            })
        }
    }

    setInsertCollapseTimer(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid])){
            const elapsedTime = this.secondsSinceInitialized(uniqueid);
            this.debugLog(elapsedTime + ' seconds elapsed between insert attached to page and insert animation start.');
            const currentInsert = this.currentInserts[uniqueid].insert;
            const calculatedCollapseTiming: number = currentInsert.insertConfigs.duration-elapsedTime;
            let collapseTime = (calculatedCollapseTiming > 5)?calculatedCollapseTiming:this.overlayCollapseTimeDelay;
            this.debugLog(`Setting collapse insert timer for insert ${uniqueid} to close in ${collapseTime} seconds.`);
            this.currentInserts[uniqueid].collapseTimer = this.createTimeout(()=>{this.insertCollapsedByTimer(uniqueid)}, collapseTime, 'CollapseTimeout_'+uniqueid);
        }
    }

    setInsertViewabilityTimer(uniqueid: string) {
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentInserts[uniqueid].viewabilityTimer = new CustomTimeout(()=>{}, 0, 'Insert Viewability Timer');
            this.currentInserts[uniqueid].viewabilityTimer.resetInterval(500, 10000, ()=>{
                if(!isNil(this.currentInserts[uniqueid]?.viewabilityTimer)){
                    if(this.currentInserts[uniqueid].viewabilityTimer.getTotalElapsedTime() >= 2000 && this.currentInserts[uniqueid].viewed === false){
                        this.dispatchInsertEvent({type:InsertEventType.INSERT_VIEWED_EVENT, uid:uniqueid, insertViewability:this.currentInserts[uniqueid].viewabilityValue});
                        this.currentInserts[uniqueid].viewed = true;
                    }
                    this.currentInserts[uniqueid].viewabilityArray.push(this.currentInserts[uniqueid].viewabilityValue);
                }
            })
            this.currentInserts[uniqueid].viewabilityTimer.pause();    
        }
    }

    setInsertLifetimeTimer(uniqueid: string) {
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentInserts[uniqueid].insertTimer = new CustomTimeout(()=>{}, 1000000, "Insert Lifetime Timer");  
        }
    }

    clearInsertCollapseTimer(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentInserts[uniqueid].collapseTimer?.cancel();
            this.currentInserts[uniqueid].collapsedByTimer = false;
            this.debugLog(`Cancelling collapse timer for insert ${uniqueid}`);
        }
    }

    createTimeout(callback:Function, seconds:number, name: string = 'CustomInsertLogicTimeout'): CustomTimeout{
        return (new CustomTimeout(callback, seconds*1000, name, this.lm, LogType.QA));
    }

    attachVideoEventListeners(uniqueid: string, vid:HTMLVideoElement){
        if(!isNil(this.currentInserts[uniqueid])){
            const currentInsert = this.currentInserts[uniqueid];
            const eventTrackers: {[key:string]:string[]} = currentInsert.eventTrackers;
            this.debugLog(['Video event trackers: ', currentInsert.eventTrackers], LogType.INSERT);
            //firstQuartile, midpoint, thirdQuartile, complete, mute, unmute, pause, resume, fullscreen, close
            (vid as any).trackerValues = {};
            const vidTracker = (vid as any).trackerValues;
            vidTracker.firstQuartile = false;
            vidTracker.midpoint = false;
            vidTracker.thirdQuartile = false;
            vidTracker.completed = false;
            vid.addEventListener('timeupdate', ()=>{
                const completionPercent = vid.currentTime/vid.duration;
                this.debugLog('Video completion percent: ' + completionPercent, [LogType.INSERT]);
                if(completionPercent > .75 && !vidTracker.thirdQuartile){
                    vidTracker.thirdQuartile = true;
                    this.debugLog('Video completes third quartile: ' + completionPercent, [LogType.IMPORTANT, LogType.QA, LogType.INSERT]);
                    this.attachEventTracker(currentInsert.insertDocument, eventTrackers, 'thirdQuartile');
                }else if(completionPercent > .5 && !vidTracker.midpoint){
                    vidTracker.midpoint = true;
                    this.debugLog('Video completes midpoint: ' + completionPercent, [LogType.IMPORTANT, LogType.QA, LogType.INSERT]);
                    this.attachEventTracker(currentInsert.insertDocument, eventTrackers, 'midpoint');
                }else if(completionPercent > .25 && !vidTracker.firstQuartile){
                    vidTracker.firstQuartile = true;
                    this.debugLog('Video completes first quartile: ' + completionPercent, [LogType.IMPORTANT, LogType.QA, LogType.INSERT]);
                    this.attachEventTracker(currentInsert.insertDocument, eventTrackers, 'firstQuartile');
                }
            })
            vid.addEventListener('ended', ()=>{
                if(!vidTracker.completed){
                    vidTracker.complete = true;
                    this.debugLog('Video complete: ' + vid.currentTime + ' = ' + vid.duration, [LogType.IMPORTANT, LogType.QA, LogType.INSERT]);
                    this.attachEventTracker(currentInsert.insertDocument, eventTrackers, 'complete');
                }
            })
            vid.addEventListener('volumechange', ()=>{
                this.debugLog('Video audio toggle: is muted? ' + vid.muted, [LogType.IMPORTANT, LogType.QA, LogType.INSERT]);
                if(vid.muted){
                    this.attachEventTracker(currentInsert.insertDocument, eventTrackers, 'mute');
                }else{
                    this.attachEventTracker(currentInsert.insertDocument, eventTrackers, 'unmute');
                }
            })
            vid.addEventListener('pause', ()=>{
                if(vid.currentTime < vid.duration){
                    this.debugLog('Video paused.', [LogType.IMPORTANT, LogType.QA, LogType.INSERT]);
                    this.attachEventTracker(currentInsert.insertDocument, eventTrackers, 'pause');
                }
            })
            vid.addEventListener('play', ()=>{
                if(vid.currentTime > .1){
                    this.debugLog('Video resume.', [LogType.IMPORTANT, LogType.QA, LogType.INSERT]);
                    this.attachEventTracker(currentInsert.insertDocument, eventTrackers, 'resume');
                }else{
                    this.debugLog('Video play.', [LogType.IMPORTANT, LogType.QA, LogType.INSERT]);
                }
            })
        }
    }
    
    attachEventTracker(doc:Document, eventTrackers: {[key:string]:string[]}, type: string){
        let trackers: string[] = [];
        if(Object.keys(eventTrackers).indexOf(type) !== -1){
            trackers = eventTrackers[type];
        }
        if(trackers.length > 0){
            let trackerLog: string[] = ["Event trackers added. "];
            const loadTime = (new Date()).getTime();
            for(var i = 0; i < trackers.length; i++){
                const addTracker: HTMLImageElement = new Image();
                addTracker.width = 1;
                addTracker.height = 1;
                addTracker.className = 'impression imageData';
                addTracker.src =  trackers[i];
                doc.appendChild(addTracker);
                addTracker.addEventListener("load", ()=>{this.debugLog([`Event tracker (${type}) response 'load' in ${(new Date()).getTime()-loadTime}ms: `, addTracker.src], [LogType.INSERT, LogType.QA])});
                addTracker.addEventListener("error", (evt)=>{this.debugLog([`Event tracker (${type}) response 'error' in  ${(new Date()).getTime()-loadTime}ms: : `, addTracker.src, evt], [LogType.INSERT, LogType.QA])});
                trackerLog.push("Attached: " + addTracker.src);
            }
            this.debugLog(trackerLog, [LogType.INSERT, LogType.QA]);
        }else{
            this.debugLog(['Insert displayed, but no impression trackers available to load.'], [LogType.INSERT, LogType.QA]);
        }
    }

    attachViewabilityTrackers(params:InsertParams){
        const trackerList: string[] = params.viewabilityTrackers;
        if(trackerList.length > 0){
            let trackerLog: string[] = ["Insert displayed. viewability trackers - "];
            for(var i = 0; i < trackerList.length; i++){
                if(this.usedTrackersList.indexOf(trackerList[i])===-1){
                    try {
                        let addTracker: HTMLElement;
                        if(trackerList[i].indexOf('http')===0){
                            addTracker = document.createElement('img');
                            (addTracker as HTMLImageElement).width = 1;
                            (addTracker as HTMLImageElement).height = 1;
                            addTracker.className = 'impression imageData';
                            (addTracker as HTMLImageElement).src =  trackerList[i];
                        }else{
                            let doc = new DOMParser().parseFromString(trackerList[i], 'text/html');

                            let trackerElement = doc.head.firstElementChild;
        
                            addTracker = document.createElement(trackerElement.tagName)
        
                            let attributeNames = trackerElement.getAttributeNames();
        
                            attributeNames.forEach((name:string) => {
                                if(!name.includes('&')){
                                    addTracker.setAttribute(name, trackerElement.getAttribute(name))
                                }else{
                                    this.debugLog('Viewability tracker attribute is invalid: ' + name, [LogType.INSERT, LogType.QA, LogType.IMPORTANT]);
                                }
                            });
                        }

                        params.insertDocument.appendChild(addTracker);
                        trackerLog.push("Attached: " + trackerList[i]);
                        this.usedTrackersList.push(trackerList[i]);
                    }catch(err){
                        this.debugLog(['Exception Caught trying to load Viewability tracker - ' + trackerList[i], err], [LogType.INSERT, LogType.QA]);
                    }
                }else{
                    trackerLog.push("Viewability tracker not attached (already used): " + trackerList[i]);
                }
            }
            this.debugLog(trackerLog, [LogType.INSERT, LogType.QA]);
        }else{
            if(!params.insert?.state?.isPrebid){
                this.debugLog(['Insert displayed, but no viewability trackers available to load.'], [LogType.INSERT, LogType.QA]);
            }
        }
    }

    attachImpressionTrackers(params:InsertParams){
        const trackerList: string[] = params.impressionTrackers;
        if(trackerList.length > 0){
            let trackerLog: string[] = ["Insert displayed. Impression trackers - "];
            const loadTime = (new Date()).getTime();
            for(var i = 0; i < trackerList.length; i++){
                if(this.usedTrackersList.indexOf(trackerList[i])===-1){
                    const addTracker: HTMLImageElement = new Image();
                    addTracker.width = 1;
                    addTracker.height = 1;
                    addTracker.className = 'impression imageData';
                    addTracker.src =  trackerList[i];
                    params.insertDocument.appendChild(addTracker);
                    addTracker.addEventListener("load", ()=>{this.debugLog([`Impression tracker response 'load' in ${(new Date()).getTime()-loadTime}ms: `, addTracker.src], [LogType.INSERT, LogType.QA])});
                    addTracker.addEventListener("error", (evt)=>{this.debugLog([`Impression tracker response 'error' in  ${(new Date()).getTime()-loadTime}ms: : `, addTracker.src, evt], [LogType.INSERT, LogType.QA])});
                    trackerLog.push("Attached: " + addTracker.src);
                    this.usedTrackersList.push(trackerList[i]);
                }else{
                    trackerLog.push("Impression tracker not attached (already used): " + trackerList[i]);
                }
            }
            this.debugLog(trackerLog, [LogType.INSERT, LogType.QA]);
        }else{
            if(!params.insert?.state?.isPrebid){
                this.debugLog(['Insert displayed, but no impression trackers available to load.'], [LogType.INSERT, LogType.QA]);
            }
        }
    }

    attachJSTracker(params:InsertParams){
        const trackerList: string[] = params.jsTrackers;
        if(trackerList.length > 0){
            let trackerLog: string[] = ["Insert displayed. JS trackers - "];
            for(var i = 0; i < trackerList.length; i++){
                if(this.usedTrackersList.indexOf(trackerList[i])===-1){
                    const addTracker = document.createElement('script')
                    addTracker.setAttribute('async', '')
                    addTracker.type = 'text/javascript';
                    const trackerSrc = trackerList[i].split("src=");
                    let trackerURL: string[] = [];
                    if(trackerSrc.length > 1){
                        trackerURL = (trackerSrc[1].split('\'').length>1)?trackerSrc[1].split('\''):trackerSrc[1].split('"');
                    }
                    if(trackerURL.length > 1){
                        addTracker.src = trackerURL[1].replace(/\\|"/g, '');
                    }else{
                        addTracker.src = trackerList[i];
                    }
                    params.insertDocument.appendChild(addTracker);
                    trackerLog.push("Attached: " + addTracker.src);
                    this.usedTrackersList.push(trackerList[i]);
                }else{
                    trackerLog.push("JS tracker not attached (already used): " + trackerList[i]);
                }
            }
            this.debugLog(trackerLog, [LogType.INSERT, LogType.QA]);
        }else{
            if(!params.insert?.state?.isPrebid){
                this.debugLog(['Insert displayed, but no JS trackers available to load.'], [LogType.INSERT, LogType.QA]);
            }
        }
    }

    attachWinTracker(params:InsertParams){
        const winTracker: string = params.winTracker;
        if(winTracker !== null && winTracker !== ''){
            let trackerLog: string[] = ["Insert loaded. Win tracker - "];
            const loadTime = (new Date()).getTime();
            if(this.usedTrackersList.indexOf(winTracker)===-1){
                const addTracker: HTMLImageElement = new Image();
                addTracker.width = 1;
                addTracker.height = 1;
                addTracker.className = 'impression imageData';
                addTracker.src =  winTracker;
                params.insertDocument.appendChild(addTracker);
                addTracker.addEventListener("load", ()=>{this.debugLog([`Win tracker response 'load' in ${(new Date()).getTime()-loadTime}ms: `, addTracker.src], [LogType.CXO, LogType.QA])});
                addTracker.addEventListener("error", (evt)=>{this.debugLog([`Win tracker response 'error' in ${(new Date()).getTime()-loadTime}ms: : `, addTracker.src, evt], [LogType.CXO, LogType.QA])});
                trackerLog.push("Attached: " + addTracker.src);
                this.usedTrackersList.push(winTracker);
            }else{
                trackerLog.push("Win tracker not attached (already used): " + winTracker);
            }
            this.debugLog(trackerLog, [LogType.INSERT, LogType.QA]);
        }else{
            if(!params.insert?.state?.isPrebid){
                this.debugLog(['Insert rendered, but no win tracker available to load.'], [LogType.INSERT, LogType.QA]);
            }
        }
    }

    attachClickthroughTrackers(params:InsertParams){
        if(params.clickthroughTrackers.length > 0){
            let trackerLog: string[] = ["Clickthrough trackers attached:"];
            const loadTime = (new Date()).getTime();
            for(var i = 0; i < params.clickthroughTrackers.length; i++){
                const addTracker: HTMLImageElement = new Image();
                addTracker.width = 1;
                addTracker.height = 1;
                addTracker.className = 'impression imageData'
                addTracker.src =  params.clickthroughTrackers[i]
                params.insertDocument.appendChild(addTracker);
                addTracker.addEventListener("load", ()=>{this.debugLog([`Clickthrough tracker response 'load' in ${(new Date()).getTime()-loadTime}ms: `, addTracker.src], [LogType.INSERT, LogType.QA])})
                addTracker.addEventListener("error", (evt)=>{this.debugLog([`Clickthrough tracker response 'error' in ${(new Date()).getTime()-loadTime}ms: `, addTracker.src, evt], [LogType.INSERT, LogType.QA])})
                trackerLog.push(addTracker.src);
            }
            this.debugLog(trackerLog, [LogType.INSERT, LogType.QA]);
        }else{
            this.debugLog(['No clickthrough trackers available to load.'], [LogType.INSERT, LogType.QA]);
        }
    }

    insertClosed(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid]) && !this.insertsPaused){
            const destroyDelay: number = (!this.currentInserts[uniqueid].insert.state.destroyInsertOnClose)?this.destroyTimeDelay:0;
            this.debugLog(['Insert minimized. Insert will be destroyed in '+destroyDelay+' seconds.'], [LogType.QA, LogType.INSERT]);
            this.dispatchInsertEvent({type:InsertEventType.INSERT_CLOSED, uid:uniqueid, insertViewability:this.currentInserts[uniqueid].viewabilityValue});            
            const cb = this.currentInserts[uniqueid].timeoutDestroy;
            gsap.delayedCall(destroyDelay, cb);
        }
    }

    getVideoBounds(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid])){
            let currentDocument = this.currentInserts[uniqueid].insertDocument;
            var vid = currentDocument.getElementById('container');
            var bounds = (!isNil(vid))?vid.getBoundingClientRect():null;
            return bounds;
        }
    }

    createScaledImage(uniqueid: string, elem1: HTMLImageElement | string, pan: HTMLElement | string, id: string, w: number, double: boolean=false): HTMLCanvasElement{
        if(!isNil(this.currentInserts[uniqueid])){
            let currentDocument = this.currentInserts[uniqueid].insertDocument;
            let can: HTMLCanvasElement = currentDocument.getElementById(id) as HTMLCanvasElement;
            let element1: CanvasImageSource = (typeof elem1 === 'string')?currentDocument.getElementById(elem1) as HTMLImageElement:elem1;
            let parent: HTMLElement = (typeof pan === 'string')?currentDocument.getElementById(pan):pan;
            let eRatio = element1.naturalHeight/element1.naturalWidth;
            let eH = w*eRatio;

            if(isNil(can)){
                can = document.createElement('canvas');
                can.width = Math.floor(w*2);
                can.height = Math.floor(eH*2);
                can.id = id;
                parent.appendChild(can);
            }

            let ctx: CanvasRenderingContext2D = can.getContext('2d');
            ctx.clearRect(0,0,can.width, can.height);

            ctx.globalCompositeOperation = 'source-out';
            ctx.drawImage(element1, this.getStyle(uniqueid, element1,'left'), this.getStyle(uniqueid, element1,'top'), can.width, can.height);
            var d: number = (double)?2:1;
            var o: number = (double)?0:1;
            gsap.set(can, {width:w*d+'px', height:eH*d+'px', opacity:o});
            return can;
        }
    }

    createLogoCanvas(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid])){
            let currentDocument = this.currentInserts[uniqueid].insertDocument;

            let logoImage: HTMLImageElement = currentDocument.getElementById('cxLogoImage') as HTMLImageElement;
            let logoPlacement: HTMLElement = currentDocument.getElementById('insertLogoClickthroughHolder');

            let lw = this.logoWidth;
            let can: HTMLCanvasElement = this.createScaledImage(uniqueid, logoImage, logoPlacement, 'cxLogo', lw);

            this.debugLog(['Created logo: ', can], LogType.INSERT);

            return can;
        }

        return null;
    }

    createPrivacyLinkout(uniqueid: string, container: HTMLElement): HTMLDivElement{
        if(!isNil(this.currentInserts[uniqueid])){
            if(!isNil(container)){
                const privacyDiv:HTMLDivElement = document.createElement("div");
                privacyDiv.id = 'privacyPolicy';
                privacyDiv.innerHTML = 'PRIVACY';
                privacyDiv.setAttribute('style', `position:absolute; top:0px; left:0px; margin:0px; padding:5px 10px; font-size:12px; font-family: Frutiger, "Frutiger Linotype", Univers, Calibri, "Gill Sans", "Gill Sans MT", "Myriad Pro", Myriad, "DejaVu Sans Condensed", "Liberation Sans", "Nimbus Sans L", Tahoma, Geneva, "Helvetica Neue", Helvetica, Arial, sans-serif; background:rgba(0,0,0,.6); opacity:1; z-index:10100; color:rgb(255,255,255)`);

                container.appendChild(privacyDiv);
                this.debugLog(["createPrivacyLinkout - Adding privacy policy button to container ", container]);
                return privacyDiv;
            }
        }
        return null;
    }

    createLoader(uniqueid: string, container: HTMLElement){
        if(!isNil(this.currentInserts[uniqueid])){
            if(!isNil(container)){
                const currentDocument = this.currentInserts[uniqueid].insertDocument;
                const loaderSize: number = 300;
                const circleSize: number = 50;
                const circleCount: number = 14;
                const circleOffset: number = (loaderSize-circleSize)*.5;
                const transitionSpeed: number = .1;
                const animationTime: number = circleCount*transitionSpeed;
                const degreeDistance: number = 360/circleCount;

                const color1: {[key:string]:number} = {r:251,g:180,b:49};
                const color2: {[key:string]:number} = {r:90,g:80,b:250};

                const loader:HTMLDivElement = document.createElement("div");
                loader.id = 'loader';
                const containerHeight: number = this.getStyle(uniqueid, container, 'height');
                const scaleHeight: number = (containerHeight>90)?90:containerHeight;
                const top: number = Math.floor((containerHeight-loaderSize)*.5);
                const left: number = Math.floor((this.getStyle(uniqueid, container, 'width')-loaderSize)*.5);
                const marginAmount: number = (containerHeight <= 60)?1.2:1.75;
                const scale: number = (scaleHeight/(loaderSize*marginAmount));
                loader.setAttribute('style', `position:absolute; margin:auto; left:${left}px; top:${top}px; scale:${scale}; opacity:1; z-index:10; width:${loaderSize}px; height:${loaderSize}px`);

                for(var i = 0; i < circleCount; i++){
                    const rads: number = (degreeDistance*i-90)*(Math.PI/180);
                    const x: number = circleOffset*Math.cos(rads)+circleOffset;
                    const y: number = circleOffset*Math.sin(rads)+circleOffset;
                    const circ:HTMLDivElement = document.createElement("div");
                    circ.id = 'circ'+i;
                    const perct: number = (i+1)/circleCount;
                    const opac: number = (this.isMobile)?perct:1;
                    //const opac: number = (this.isMobile)?perct+(1-perct)*.5:1;
                    //const clrr: number = (this.isMobile)?(color1.r*perct) + (color2.r*(1-perct)):color1.r;
                    //const clrg: number = (this.isMobile)?(color1.g*perct) + (color2.g*(1-perct)):color1.g;
                    //const clrb: number = (this.isMobile)?(color1.b*perct) + (color2.b*(1-perct)):color1.b;

                    circ.setAttribute('style', `position:absolute; display:block; opacity:${opac}; background:rgb(${color1.r}, ${color1.g}, ${color1.b}); width:${circleSize}px; height:${circleSize}px; border-radius:${circleSize}px`);
                    gsap.set(circ, {x:x, y:y});
                    //gsap.set(circ, {position:'absolute', display:'block', opacity:opac, background: `rgb(${color1.r}, ${color1.g}, ${color1.b})`, width:circleSize+'px', height:circleSize+'px', borderRadius:circleSize+'px', x:x, y:y});
                    loader.appendChild(circ);
                }

                let continueAnimation: boolean = true;
                const animateLdr = (num:number)=>{
                    if(num === circleCount){
                        num = 0;
                    }
                    const circ: HTMLElement = currentDocument.getElementById('circ'+num) as HTMLElement;
                    continueAnimation=(isNil(circ))?false:true;
                    if(continueAnimation && !this.isMobile){
                        gsap.fromTo(circ, {background:`rgb(${color1.r},${color1.g},${color1.b})`, scale:0, opacity:1}, {scale:1, ease:Back.easeOut.config(1), duration: animationTime*.1});
                        gsap.to(circ, {background:`rgb(${color2.r},${color2.g},${color2.b})`, ease:Power1.easeInOut, delay:animationTime*.25, duration:animationTime*.35});
                        gsap.to(circ, {opacity:0, scale:.25, ease:Power1.easeIn, delay:animationTime*.6, duration:animationTime*.4});
                        gsap.delayedCall(transitionSpeed, animateLdr, [++num]);
                    }
                }
                let rot = 0;
                const spinLoader = ()=>{
                    rot = (rot>=345)?0:rot+15;
                    if(continueAnimation && this.isMobile){
                        gsap.set(loader, {rotation:rot});
                        gsap.delayedCall(.1, spinLoader);
                    }
                }

                container.appendChild(loader);
                this.debugLog(["createLoader - Adding loader animation to container ", container]);
                
                if(!this.isMobile){
                    gsap.delayedCall(.1, animateLdr, [0]); 
                }else{
                    spinLoader();   
                }
            }
        }
    }

    idCheck(id:string):boolean{
        //this.debugLog('Insert list: '+this.currentIDs);
        return (this.currentIDs.indexOf(id)>=0)?true:false;
    }

    insertIsDestroying(id:string):boolean{
        let destroying: boolean = false;
        if(this.idCheck(id)){
            destroying = this.currentInserts[id].isDestroying;
        }
        return destroying;
    }

    /* =========== COLORING FUNCTIONS =========== */

    changeImageColor(uniqueid, elem1, clr, pan, id) {
        if(!isNil(this.currentInserts[uniqueid])){
            let currentDocument = this.currentInserts[uniqueid].insertDocument;
            let can: HTMLCanvasElement = currentDocument.getElementById(id) as HTMLCanvasElement;
            let element1 = currentDocument.getElementById(elem1) as HTMLImageElement;
            let w = this.getStyle(uniqueid, element1, 'width');
            let h = this.getStyle(uniqueid, element1, 'height');

            try{
                if (can == null) {
                    //can.id = 'null';
                    //document.getElementById(pan).removeChild(can);
                    can = document.createElement('canvas');
                    can.width = w;
                    can.height = h;
                    can.id = id;
                    currentDocument.getElementById(pan).appendChild(can);
                }

                var ctx = can.getContext('2d');
                ctx.save();
                ctx.fillStyle = clr;
                ctx.fillRect(0, 0, can.width, can.height);
                ctx.globalCompositeOperation = 'destination-in';
                ctx.drawImage(element1, this.getStyle(uniqueid, element1, 'left'), this.getStyle(uniqueid, element1, 'top'));
                ctx.restore();
                gsap.set(can, {position: 'absolute'});
            }catch(err){
                this.debugLog("Error occured on changing element color: " + elem1.id, [LogType.INSERT]);
                return elem1;
            }
            return can;
        }
    }

    //colorStepDown = (clr, amt) => ([...clr].map(x => {return (x > amt) ? x - .5*amt-Math.floor(Math.random()*.5*amt) : 0;}));
    colorStepDown = (clr:number[], amt:number):number[] => ([...clr].map(x => {return (x > amt) ? x - amt : 0;}));

    //colorStepUp = (clr, amt) => ([...clr].map(x =>{return (x < 255 - amt) ? x + .5*amt+Math.floor(Math.random()*.5*amt) : 255;}));
    colorStepUp = (clr:number[], amt:number):number[] => ([...clr].map(x =>{return (x < 255 - amt) ? x + amt : 255;}));

    colorStepDownProportional = (clr:number[], amt:number):number[] => ([...clr].map(x =>{return (x - (x * amt/100) > 0) ? Math.floor(x-(x*amt/100)) : 0;}));

    colorStepUpProportional = (clr:number[], amt:number):number[] => ([...clr].map(x =>{return (x + (x * amt/100) < 255) ? Math.ceil(x+(x*amt/100)) : 255;}));

    rgbToHex = (r:number, g:number, b:number):string => '#' + [r, g, b].map(x => {
        const hex = x.toString(16)
        return hex.length === 1 ? '0' + hex : hex
    }).join('');

    luminance(r:number, g:number, b:number):number {
        var a = [r, g, b].map(function(v) {
            v /= 255;
            return v <= 0.03928 ?
                v / 12.92 :
                Math.pow((v + 0.055) / 1.055, 2.4);
        });
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    }

    contrast(rgb1:number[], rgb2:number[]):number {
        var lum1 = this.luminance(rgb1[0], rgb1[1], rgb1[2]);
        var lum2 = this.luminance(rgb2[0], rgb2[1], rgb2[2]);
        var brightest = Math.max(lum1, lum2);
        var darkest = Math.min(lum1, lum2);
        return ((brightest + 0.05) / (darkest + 0.05));
    }

    getColorPalette(elem):number[][]{
        let cp: number[][];
        try{
            cp = this.colorThief.getPalette(elem, 8, 2, false, false);
        }catch(err){
            this.debugLog(['Error pulling color data from image', elem, err], [LogType.ERROR, LogType.INSERT]);
        }
        cp = (isNil(cp)||cp.length === 0)?[[0,0,0],[255,255,255]]:cp;
        return cp;
    }

    /* =========== EVENT LISTENERS AND EVENT HANDLERS =========== */

    overlayCommunicationEvent(evt) {
        evt.stopPropagation();
        if(evt.detail.type !== OverlayEventType.OVERLAY_RESIZE){
            this.debugLog(`communication from overlay: ${evt.detail.type} for insert ${evt.detail.uid} on insert list ${this.currentIDs}`);
        }
        switch (evt.detail.type) {
            case OverlayEventType.OVERLAY_REQUEST_DESTROY: {
                if(!isNil(evt.detail.uid)){
                    if(this.currentIDs.length === 0){
                        this.insertDestroyFunction('empty');
                    }else{
                        this.insertDestroyFunction(evt.detail.uid);
                    }

                }
                break;
            }
            case OverlayEventType.OVERLAY_DESTROY_REMAINING_INSERTS: {
                this.currentIDs.forEach(ins => {
                    this.debugLog(`Removing insert ${ins} in DESTROY REMAINING INSERTS`, [LogType.INSERT, LogType.QA]);
                    this.insertDestroyFunction(ins);
                });
                break;
            }
            case OverlayEventType.OVERLAY_RESIZE:{
                let currentInsertList: string[] = this.currentIDs;
                currentInsertList.forEach(ins =>{
                    this.currentInserts[ins]?.resizeTimer.execute();
                });
                break;
            }
            case OverlayEventType.OVERLAY_PAUSE_INSERTS:{
                this.resumeInsertsTimeout?.cancel();
                this.currentIDs.forEach(ins => {
                    if(this.currentInserts[ins]?.insert?.isSubloaded === false){
                        const activeInsert = this.currentInserts[ins];
                        if(typeof activeInsert?.collapseTimer !== 'undefined' && activeInsert.collapseTimer !== null){
                            this.debugLog(`Pausing insert ${ins}.`);
                            activeInsert.collapseTimer.pause();
                            if(activeInsert.insert.el.tagName.toLowerCase().indexOf('squeeze') !== -1){
                                activeInsert.insert.endInsert();
                            }
                            this.pauseInsertTimers(ins);
                        }
                    }
                    if(typeof this.currentInserts[ins]?.insert?.fontComplete !== 'undefined' && this.currentInserts[ins].insert.fontComplete !== null){
                        this.debugLog(`Unsetting fontComplete on insert ${ins}.`);
                        this.currentInserts[ins].insert.fontComplete = false;
                    }
                })
                this.currentDisplayCompleteTimer.pause();
                this.insertsPaused = true;
                break;
            }
            case OverlayEventType.OVERLAY_RESUME_INSERTS:{
                //Adding delay ot restarting insert in case video elements need time to be resized/added to stage
                this.resumeInsertsTimeout.reset(250, ()=>{
                    this.currentDisplayCompleteTimer.resume();
                    this.insertsPaused = false;

                    const activeInserts: string[] = this.currentIDs.filter(x => this.currentInserts[x].insert.isSubloaded === false);
                    const subactiveInserts: string[] = this.currentIDs.filter(x => this.currentInserts[x].insert.isSubloaded === true);
                    let subloadTriggered: boolean = false;
                    if(subactiveInserts.length > 0){
                        this.debugLog('CX-Wait ended - Trying SUBLOAD on insert: ' + subactiveInserts[0], LogType.INSERT);
                        subloadTriggered = this.showSubloadedInsert(subactiveInserts[0], true);
                    }
                    this.debugLog(`CX-Wait ended - Was subload insert triggered on midroll end? (${subloadTriggered})  - should active insert continue? (${evt.detail.continue})`, LogType.INSERT);
                    if(!subloadTriggered){
                        activeInserts.forEach((activeInsert: string) =>{
                            if(activeInsert != null && evt.detail.continue && typeof this.currentInserts[activeInsert].collapseTimer !== 'undefined' && this.currentInserts[activeInsert].collapseTimer !== null){
                                this.debugLog(`Resuming insert ${activeInsert}.`);
                                this.currentInserts[activeInsert].collapseTimer.addToRemainingTime(1);
                                this.currentInserts[activeInsert].collapseTimer.resume();
                                if(this.currentInserts[activeInsert].insert.el.tagName.toLowerCase().indexOf('squeeze') !== -1 && !this.currentInserts[activeInsert].closeTriggered && !this.currentInserts[activeInsert].collapsedByTimer){
                                    this.currentInserts[activeInsert].insert.initializeInsertContent();
                                }
                                this.resumeInsertTimers(activeInsert);
                            }
                        })
                    }
                });
                break;
            }
            case OverlayEventType.OVERLAY_REFRESH_ACTIVE_OSD:{
                const activeInserts: string[] = this.currentIDs.filter(x => this.currentInserts[x].insert.isSubloaded === false);
                activeInserts.forEach((activeInsert: string) =>{
                    if(activeInsert != null && this.currentInserts[activeInsert].insert?.state?.isPrebid){
                        if(!this.currentInserts[activeInsert].insert?.insertClosed){
                            this.debugLog(`OVERLAY_REFRESH_ACTIVE_OSD - Refreshing Prebid Carousel - ${activeInsert}.`, LogType.INSERT);
                            this.currentInserts[activeInsert].insert.insertReloading = true;
                            this.currentInserts[activeInsert].insert.refreshPrebidContainer?.();
                        }else if(evt.detail?.forceRefresh){
                            this.debugLog(`OVERLAY_REFRESH_ACTIVE_OSD - Prebid carousel refresh requested but insert is closed. ForceRefresh is true. Will re-open insert. - ${activeInsert}.`, LogType.INSERT);
                            this.resetInitializedTime(activeInsert);
                            this.currentInserts[activeInsert].insert.initializeInsertContent();
                            this.debugLog(`OVERLAY_REFRESH_ACTIVE_OSD - Refreshing Prebid Carousel - ${activeInsert}.`, LogType.INSERT);
                            this.currentInserts[activeInsert].insert.insertReloading = true;
                            this.currentInserts[activeInsert].insert.refreshPrebidContainer?.();
                        }else{
                            this.debugLog(`OVERLAY_REFRESH_ACTIVE_OSD - Prebid carousel refresh requested but insert is closed. Will not refresh. - ${activeInsert}.`, LogType.INSERT);
                        }
                    }
                })
                
                break;
            }
            case OverlayEventType.OVERLAY_RESET_ACTIVE_OSD:{
                if(!isNil(evt.detail.uid)){
                    const osdInsert = this.currentInserts[evt.detail.uid];
                    if(!isNil(osdInsert)){

                        const insertLifetime = osdInsert.insertTimer?.getTotalElapsedTime()/1000 || 0;
                        const insertViewabilityTime = osdInsert.viewabilityTimer?.getTotalElapsedTime()/1000 || 0;
                        const insertAverageViewabilityValue = (osdInsert.viewabilityArray.length > 0)?osdInsert.viewabilityArray.reduce((x, y) => x + y) / osdInsert.viewabilityArray.length:0;
            
                        osdInsert?.viewabilityTimer?.cancel?.();
                        osdInsert?.insertTimer?.cancel?.();
                        osdInsert?.collapseTimer?.cancel?.();
            
                        this.dispatchInsertEvent({
                            type:InsertEventType.INSERT_GPT_RESET,
                            uid:evt.detail.uid,
                            insertLifetime:insertLifetime,
                            insertViewabilityTime:insertViewabilityTime,
                            insertAverageViewabilityValue: insertAverageViewabilityValue
                        });
                    }
                }
                break;
            }
            default:{
                let currentInsertList = this.currentIDs;
                for(let i = 0; i < currentInsertList.length; i++){
                    this.currentInserts[currentInsertList[i]].insert.incomingEvent(evt.detail);
                }
                break;
            }
        }
    }

    addListeners(btn:HTMLElement) {
        //debugLog('ADDING EVENT LISTENERS: ' + btn.id);
        gsap.set(btn, { cursor: 'pointer' });
        btn.addEventListener('click', this.boundButtonClick);
        btn.addEventListener('mouseover', this.boundButtonOver);
        btn.addEventListener('touchstart', this.boundTouchStart);
        btn.addEventListener('touchend', this.boundTouchEnd);
        btn.addEventListener('mouseout', this.boundButtonOut);
        btn.addEventListener('mousedown', this.boundButtonDown);
        btn.addEventListener('mouseup', this.boundButtonUp);
        btn.addEventListener('contextmenu', this.boundPreventMenu);
    }
    removeListeners(btn:HTMLElement) {
        //dd_log(btn);
        gsap.set(btn, { cursor: 'default' });
        btn.removeEventListener('click', this.boundButtonClick);
        btn.removeEventListener('mouseover', this.boundButtonOver);
        btn.removeEventListener('touchstart', this.boundTouchStart);
        btn.removeEventListener('touchend', this.boundTouchEnd);
        btn.removeEventListener('mouseout', this.boundButtonOut);
        btn.removeEventListener('mousedown', this.boundButtonDown);
        btn.removeEventListener('mouseup', this.boundButtonUp);
        btn.removeEventListener('contextmenu', this.boundPreventMenu);
    }

    buttonDown(evt) {
        if (evt) {
            evt.stopPropagation();
        }
        //const t = evt.target || evt.srcElement;
        //this.debugLog(['Button down: ' + t.id], LogType.IMPORTANT);
        //let t = evt.target || evt.srcElement;
        //debugLog('button down event: ' + t.id);
    }
    buttonUp(evt) {
        if (evt) {
            evt.stopPropagation();
        }
        //let t = evt.target || evt.srcElement;
        //debugLog('button up event: ' + t.id);
    }

    buttonClick(evt) {
        if (evt) {
            evt.stopPropagation();
        }
        const currentInsertList = this.currentIDs;
        //this.debugLog(['Button click: ' + t.id], LogType.IMPORTANT);
        for(let i = 0; i < currentInsertList.length; i++){
            if(this.currentInserts[currentInsertList[i]].insertDocument.contains(evt.target)){
                this.debugLog([`Button click event triggered from element '${evt.currentTarget?.id}' on insert:`, this.currentInserts[currentInsertList[i]].insert], [LogType.INSERT]);
               // if(isNil(this.touchTarget)){
                    this.buttonClickAction(evt, currentInsertList[i], this.currentInserts[currentInsertList[i]].insert);
                //}
                this.touchTarget = null;
                break;
            }
        }
    }

    buttonClickAction(evt, currentInsertId: string, currentInsert: any){
        const t = evt.target || evt.srcElement;
        const currentDocument:Document = this.currentInserts[currentInsertId].insertDocument
        if(typeof currentInsert.buttonClick !== 'undefined'){
            this.debugLog('Overriding button click function');
            currentInsert.buttonClick(evt);
        }else{
            this.debugLog('No overriding button click function: ' + currentInsertId);
            if (t == currentDocument.getElementById('clickthroughHotspot') || t == currentDocument.getElementById('clickthroughHotspotHorizontal') || t == currentDocument.getElementById('clickthroughHotspotVertical') || t == currentDocument.getElementById('clickthroughHotspotRail1') || t == currentDocument.getElementById('clickthroughHotspotRail2')) {
                this.nativeClickthrough(currentInsertId);
            } else if (t == currentDocument.getElementById('clickthroughHotspot2')) {
                this.cxLogoClick(currentInsertId)
            } else if (t == currentDocument.getElementById('clickthroughHotspot3')) {
                this.cxOutbrainClick(currentInsertId)
            } else if (t == currentDocument.getElementById('clickthroughHotspot4')) {
                this.privacyClickthrough(currentInsertId)
            } else if (t == currentDocument.getElementById('closeHotspot')) {
                this.closeButtonClick(currentInsertId);
            }
        }
    }

    nativeClickthrough(uid: string){
        if(this.idCheck(uid)){
            const currentDocument = this.currentInserts[uid].insertDocument;
            this.debugLog('Insert clickthrough triggered.', [LogType.QA, LogType.INSERT]);
            this.attachClickthroughTrackers(this.currentInserts[uid]);
            this.endInsert(uid);
            this.sendAnalytics(InsertAnalyticsEventType.CLICKTHROUGH, uid, this.currentInserts[uid].viewabilityValue);
            currentDocument.getElementById('container_clickthrough').click();
        }
    }

    bannerClickthrough(uid: string){
        if(this.idCheck(uid)){
            this.debugLog('Banner insert clickthrough detected.', [LogType.QA, LogType.INSERT]);
            this.attachClickthroughTrackers(this.currentInserts[uid]);
            this.endInsert(uid);
            this.sendAnalytics(InsertAnalyticsEventType.CLICKTHROUGH, uid, this.currentInserts[uid].viewabilityValue);
        }
    }

    privacyClickthrough(uid: string){
        if(this.idCheck(uid)){
            const currentDocument = this.currentInserts[uid].insertDocument;
            this.debugLog('Insert privacy clickthrough triggered.', [LogType.QA, LogType.INSERT]);
            this.sendAnalytics(InsertAnalyticsEventType.PRIVACY_CLICKTHROUGH, uid, this.currentInserts[uid].viewabilityValue);
            currentDocument.getElementById('container_clickthrough4').click();
        }
    }

    cxLogoClick(uid: string){
        if(this.idCheck(uid)){
            const currentDocument = this.currentInserts[uid].insertDocument;
            const currentInsert = this.currentInserts[uid].insert;
            if (currentInsert.insertDisplayed) {
                if(currentInsert.insertConfigs.isBrainly){
                    this.debugLog('Brainly X clicked. Closing insert.', [LogType.QA, LogType.INSERT]);
                    this.closeButtonClick(uid);
                }else if(currentInsert.insertConfigs.isOutbrain){
                    this.debugLog('Adsense logo clicked. Clickthrough triggered to Outbrain.', [LogType.QA, LogType.INSERT]);
                    this.endInsert(uid);
                    this.sendAnalytics(InsertAnalyticsEventType.OUTBRAIN_CLICKTHROUGH, uid, this.currentInserts[uid].viewabilityValue);
                    currentDocument.getElementById('container_clickthrough2').click();
                }else{
                    if(currentInsert.insertConfigs.isBranded){
                        this.debugLog('CatapultX logo clicked. Clickthrough triggered to CX site.', [LogType.QA, LogType.INSERT]);
                        this.endInsert(uid);
                        this.sendAnalytics(InsertAnalyticsEventType.LOGO_CLICKTHROUGH, uid, this.currentInserts[uid].viewabilityValue);
                        currentDocument.getElementById('container_clickthrough2').click();
                    }else{
                        this.debugLog('Unbranded logo clicked. No clickthrough will be initiated.', [LogType.QA, LogType.INSERT]);
                    }
                } 
            } else {
                this.debugLog('CatapultX logo clicked. Closed insert will re-open.', [LogType.QA, LogType.INSERT]);
                this.sendAnalytics(InsertAnalyticsEventType.USER_EXPANDED, uid, this.currentInserts[uid].viewabilityValue);
                this.dispatchInsertEvent({type:InsertEventType.INSERT_OPENED, uid:uid, insertViewability:this.currentInserts[uid].viewabilityValue});
                this.clearInsertCollapseTimer(uid)
                currentInsert.initializeInsertContent();
                if(!isNil(currentInsert.insertReopenClicked)){
                    currentInsert.insertReopenClicked();
                }
                this.resumeInsertTimers(uid);
            }
        }
    }

    cxOutbrainClick(uid: string){
        if(this.idCheck(uid)){
            const currentDocument = this.currentInserts[uid].insertDocument;
            const currentInsert = this.currentInserts[uid].insert;
            if (!currentInsert.insertClosed) {
                this.debugLog('Adsense logo clicked. Clickthrough triggered to Outbrain.', [LogType.QA, LogType.INSERT]);
                this.endInsert(uid);
                this.sendAnalytics(InsertAnalyticsEventType.OUTBRAIN_CLICKTHROUGH, uid, this.currentInserts[uid].viewabilityValue);
                currentDocument.getElementById('container_clickthrough3').click();
            }
        }
    }

    closeButtonClick(uid: string){
        if(this.idCheck(uid)){
            this.debugLog('User close button triggered. ' + uid, [LogType.QA, LogType.INSERT]);
            this.currentInserts[uid].closeTriggered = true;
            this.dispatchInsertEvent({type:InsertEventType.INSERT_USER_CLOSED, uid:uid, insertViewability:this.currentInserts[uid].viewabilityValue});
            this.endInsert(uid);
        }
    }

    buttonOver(evt) {
        if (evt) {
            evt.stopPropagation();
        }
        const currentInsertList = this.currentIDs;
        for(let i = 0; i < currentInsertList.length; i++){
            if(this.currentInserts[currentInsertList[i]].insertDocument.contains(evt.target)){
                this.debugLog([`Button over event triggered from element '${evt.currentTarget?.id}' on insert:`, this.currentInserts[currentInsertList[i]].insert], [LogType.INSERT]);

                if(isNil(this.touchTarget)){
                    this.buttonOverAction(evt, this.currentInserts[currentInsertList[i]].insertDocument, this.currentInserts[currentInsertList[i]].insert);
                }

                break;
            }
        }
    }
    
    touchStart(evt) {
        if (evt) {
            evt.stopPropagation();
        }

        const currentInsertList = this.currentIDs;
        for(let i = 0; i < currentInsertList.length; i++){
            if(this.currentInserts[currentInsertList[i]].insertDocument.contains(evt.target)){
                this.touchTarget = evt.currentTarget;
                this.debugLog([`Button touch start event triggered from element '${evt.currentTarget?.id}' on insert:`, this.currentInserts[currentInsertList[i]].insert], [LogType.INSERT]);

                this.buttonOverAction(evt, this.currentInserts[currentInsertList[i]].insertDocument, this.currentInserts[currentInsertList[i]].insert);

                break;
            }
        }
    }

    touchEnd(evt){
        if (evt) {
            evt.stopPropagation();
        }

        const currentInsertList = this.currentIDs;
        for(let i = 0; i < currentInsertList.length; i++){
            if(this.currentInserts[currentInsertList[i]].insertDocument.contains(evt.target)){
                this.debugLog([`Button touch end event triggered from element '${evt.currentTarget?.id}' on insert:`, this.currentInserts[currentInsertList[i]].insert], [LogType.INSERT]);
                if(this.touchTarget === evt.currentTarget){
                    //this.buttonClickAction(evt, currentInsertList[i], this.currentInserts[currentInsertList[i]].insert);
                }

                break;
            }
        }
    }

    buttonOverAction(evt, currentDocument:Document, currentInsert: any){
        const t = evt.target || evt.srcElement;
        if(typeof currentInsert.buttonOver !== 'undefined'){
            this.debugLog('Overriding button over function');
            currentInsert.buttonOver(evt);
        }else{
            if (t == currentDocument.getElementById('clickthroughHotspot') || t == currentDocument.getElementById('clickthroughHotspotHorizontal') || t == currentDocument.getElementById('clickthroughHotspotVertical')) {
                if (currentInsert.animationComplete) {
                    currentInsert.highlightCTA();
                }
            } else if (t == currentDocument.getElementById('closeHotspot')) {
                gsap.to(currentDocument.getElementById('insertCloseImg'), {duration:.4, rotation: 90, ease: Back.easeOut });
            }
        }
    }

    buttonOut(evt) {
        if (evt) {
            evt.stopPropagation();
        }
        const currentInsertList = this.currentIDs;
        let t = evt.target || evt.srcElement;
        for(let i = 0; i < currentInsertList.length; i++){
            const currentDocument = this.currentInserts[currentInsertList[i]].insertDocument;
            if(this.currentInserts[currentInsertList[i]].insertDocument.contains(evt.target)){
                const currentInsert = this.currentInserts[currentInsertList[i]].insert;
                this.debugLog([`Button out event triggered from element '${evt.currentTarget?.id}' on insert:`, this.currentInserts[currentInsertList[i]].insert], [LogType.INSERT]);

                if(typeof currentInsert.buttonOut !== 'undefined'){
                    this.debugLog('Overriding button over function');
                    currentInsert.buttonOut(evt);
                }else{
                    if (t == currentDocument.getElementById('clickthroughHotspot') || t == currentDocument.getElementById('clickthroughHotspotHorizontal') || t == currentDocument.getElementById('clickthroughHotspotVertical')) {
                        if (currentInsert.animationComplete) {
                            currentInsert.removeHighlightCTA();
                        }
                    } else if (t == currentDocument.getElementById('closeHotspot')) {
                        gsap.to(currentDocument.getElementById('insertCloseImg'), {duration:.4, rotation: 0, ease: Back.easeOut });
                    }
                }
                break;
            }
        }
    }

    removeHotspots(uniqueid: string) {
        if(!isNil(this.currentInserts[uniqueid])){
            let currentDocument = this.currentInserts[uniqueid].insertDocument;
            let hs = currentDocument.querySelectorAll('.hotspot');
            for (let i = 0; i < hs.length; i++) {
                this.removeListeners(hs[i] as HTMLElement);
                gsap.set(hs[i], { visibility: 'hidden' });
            }
        }
    }

    initializeHotspots(uniqueid: string) {
        //debugLog('INITIALIZING HOTSPOTS');
        if(!isNil(this.currentInserts[uniqueid])){
            let currentDocument = this.currentInserts[uniqueid].insertDocument;
            let hs = currentDocument.querySelectorAll('.hotspot');
            for (let i = 0; i < hs.length; i++) {
                this.initializeHotspot(hs[i] as HTMLElement);
            }
        }
    }

    initializeHotspot(hs: HTMLElement) {
        this.addListeners(hs);
        gsap.set(hs, { visibility: 'visible' });
    }

    pauseInsertTimers(uniqueid:string){
        if(!isNil(this.currentInserts[uniqueid])) {
            this.currentInserts[uniqueid].isPaused = true;
            if(this.currentInserts[uniqueid].viewabilityTimer) {
                //edge case, usually will say "cant pause already paused timer"
                this.currentInserts[uniqueid].viewabilityTimer.pause();
            }
            if(this.currentInserts[uniqueid].insertTimer) {
                this.currentInserts[uniqueid].insertTimer.pause();
            }
        }
    }

    resumeInsertTimers(uniqueid:string){
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentInserts[uniqueid].isPaused = false;
            if(this.currentInserts[uniqueid].insertTimer) {
                this.currentInserts[uniqueid].insertTimer.resume();
            }
            if(this.currentInserts[uniqueid].viewabilityTimer && this.currentInserts[uniqueid].viewabilityValue > .5) {
                //force resume for edge case where intersection observer doesnt resume timer on its own
                this.currentInserts[uniqueid].viewabilityTimer.resume();
            }
        }
    }

    preventMenu(evt:Event) {
        if (this.preventRightClickMenu && !this.isLocal) {
            evt.preventDefault();
            this.buttonClick(evt);
        }
    }

    insertDestroyFunction(uniqueid: string, timeoutDestroy: boolean = false){
        this.debugLog('Destroy insert requested: ' + uniqueid, [LogType.INSERT]);
        if(!isNil(this.currentInserts[uniqueid])){
            const closeTriggered: boolean = this.currentInserts[uniqueid].closeTriggered;
            const currentInsert = this.currentInserts[uniqueid].insert;
            const insertLifetime = this.currentInserts[uniqueid].insertTimer?.getTotalElapsedTime()/1000 || 0;
            const insertViewabilityTime = this.currentInserts[uniqueid].viewabilityTimer?.getTotalElapsedTime()/1000 || 0;
            const insertAverageViewabilityValue = (this.currentInserts[uniqueid].viewabilityArray.length > 0)?this.currentInserts[uniqueid].viewabilityArray.reduce((x, y) => x + y) / this.currentInserts[uniqueid].viewabilityArray.length:0;
            gsap.killTweensOf(this.currentInserts[uniqueid].timeoutDestroy);
            this.endInsert(uniqueid);
            this.currentInserts[uniqueid].insert.insertClosed = true;
            this.currentInserts[uniqueid].isDestroying = true;

            this.removeCurrentInsert(uniqueid);

            const isADX: boolean = (!isNil(currentInsert) && !isNil(currentInsert.isADX))?currentInsert.isADX:false;
            this.dispatchInsertEvent({
                type: InsertEventType.INSERT_DESTROYED,
                uid: uniqueid,
                destroyAll: (closeTriggered && timeoutDestroy)?true:false ,
                isADX: isADX,
                insertLifetime:insertLifetime,
                insertViewabilityTime:insertViewabilityTime,
                insertAverageViewabilityValue: insertAverageViewabilityValue});
            const debugString: string = (timeoutDestroy)?"Destroying insert element due to timeout: " + uniqueid:'Destroying insert element:' + uniqueid;
            this.debugLog([debugString, uniqueid], LogType.QA);
        }else if(uniqueid === 'empty'){
            this.debugLog(['Destroying insert element: EMPTY ID'], [LogType.QA, LogType.INSERT]);
            this.dispatchInsertEvent({type:InsertEventType.INSERT_DESTROYED, uid:uniqueid});
        }else{
            this.debugLog(['Destroying insert is not part of the active insert list: ' + uniqueid], [LogType.QA, LogType.INSERT]);
        }
    }

    endInsert(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid])){
            this.clearInsertCollapseTimer(uniqueid);
            this.pauseInsertTimers(uniqueid);
            this.debugLog('End insert: ' + uniqueid);
            const currentInsert = this.currentInserts[uniqueid].insert;
            currentInsert.endInsert();
        }
    }

    scheduleResizeInsert(uniqueid: string, delay: number = 100){
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentInserts[uniqueid]?.resizeTimer.reset(delay);
        }
    }

    resizeInsert(uniqueid: string){
        if(!isNil(this.currentInserts[uniqueid])){
            let currentDocument = this.currentInserts[uniqueid]?.insertDocument;
            if(!isNil(currentDocument) && !isNil(currentDocument.getElementById('insertHolder')) && !isNil(this.currentInserts[uniqueid].insert.insertResizeFunction)){
                if(this.currentInserts[uniqueid].insert.el.tagName.toLowerCase().includes('flap')){
                    this.currentInserts[uniqueid].insert.insertResizeFunction(true);
                } else {
                    this.currentInserts[uniqueid].insert.insertResizeFunction();
                }
            }
        }
    }

    sendAnalytics(iaet:InsertAnalyticsEventType, uid:string, insertViewability?:number){
        this.dispatchInsertEvent({type:InsertEventType.INSERT_TRACKING_EVENT, action:iaet, uid:uid, insertViewability:insertViewability});
    }

    bannerBlockedNotify(uniqueid:string){
        if(!isNil(this.currentInserts[uniqueid])){
            this.dispatchInsertEvent({type:InsertEventType.INSERT_BANNER_BLOCKED, uid:uniqueid});
        }
    }

    bannerLoadFailedNotify(uniqueid:string){
        if(!isNil(this.currentInserts[uniqueid])){
            if(this.currentInserts[uniqueid].insert.state.isADX || this.currentInserts[uniqueid].insert.state.isPrebid){
                this.dispatchInsertEvent({type:InsertEventType.INSERT_GPT_FAILED, uid:uniqueid});
            }else{
                this.dispatchInsertEvent({type:InsertEventType.INSERT_BANNER_FAILED, uid:uniqueid});
            }
        }
    }

    bannerRenderFailedNotify(uniqueid:string){
        if(!isNil(this.currentInserts[uniqueid])){
            if(this.currentInserts[uniqueid].insert.state.isADX || this.currentInserts[uniqueid].insert.state.isPrebid){
                this.dispatchInsertEvent({type:InsertEventType.INSERT_GPT_RENDER_FAILED, uid:uniqueid});
            }
        }
    }

    bannerResponseReceived(uniqueid:string, isSafeframe?: boolean){
        if(!isNil(this.currentInserts[uniqueid])){
            if(this.currentInserts[uniqueid].insert.state.isADX || this.currentInserts[uniqueid].insert.state.isPrebid){
                isSafeframe ??= false;
                this.dispatchInsertEvent({type:InsertEventType.INSERT_GPT_RESPONSE, uid:uniqueid, safeframe: isSafeframe});
            }
        }
    }

    prebidRefreshResponseReceived(uniqueid){
        if(!isNil(this.currentInserts[uniqueid])){

            const insertLifetime = this.currentInserts[uniqueid].insertTimer?.getTotalElapsedTime()/1000 || 0;
            const insertViewabilityTime = this.currentInserts[uniqueid].viewabilityTimer?.getTotalElapsedTime()/1000 || 0;
            const insertAverageViewabilityValue = (this.currentInserts[uniqueid].viewabilityArray.length > 0)?this.currentInserts[uniqueid].viewabilityArray.reduce((x, y) => x + y) / this.currentInserts[uniqueid].viewabilityArray.length:0;

            //this.currentInserts[uniqueid]?.viewabilityTimer.cancel?.();
            //this.currentInserts[uniqueid]?.insertTimer.cancel?.();
            //this.currentInserts[uniqueid]?.collapseTimer.cancel?.();

            this.dispatchInsertEvent({
                type:InsertEventType.INSERT_GPT_REFRESH,
                uid:uniqueid,
                insertLifetime:insertLifetime,
                insertViewabilityTime:insertViewabilityTime,
                insertAverageViewabilityValue: insertAverageViewabilityValue});

        }else{
            this.debugLog(`--prebidRefreshResponseReceived uniqueid ${uniqueid} does not exist in current inserts.`, [LogType.QA, LogType.INSERT]);
        }
    }

    updateInsertId(uniqueid, newid){
        if(!isNil(this.currentInserts[uniqueid])){
            this.currentInserts[newid] = this.currentInserts[uniqueid];
            
            this.currentInserts[uniqueid] = null;
            delete this.currentInserts[uniqueid];

            this.setInsertViewabilityTimer(newid);
            this.setInsertLifetimeTimer(newid);
            this.setInsertCollapseTimer(newid);
            this.initializeIntersectionObserver(this.currentInserts[newid]);
            this.currentInserts[newid].viewed = false;
            this.currentInserts[newid].viewabilityArray = [this.currentInserts[newid].viewabilityValue];
            this.currentInserts[newid].boundDestroy= this.insertDestroyFunction.bind(this, newid);
            this.currentInserts[newid].timeoutDestroy = this.insertDestroyFunction.bind(this, newid, true);
        }
    }

    dispatchInsertEvent(params: Object) {
        if(this._elementHost !== null && typeof this._elementHost !== 'undefined'){
            let insertEvent: CustomEvent = new CustomEvent(InsertEventType.EVENT_FROM_INSERT, {
                bubbles: false,
                detail: params
            })
            this._elementHost.dispatchEvent(insertEvent);
        }
    }
}
